import React from 'react';
import { useQuery } from '@tanstack/react-query';
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Thumb, matterportShowUrl } from './shared';

type Shot = { id: string; name: string; src: string };

export type MatterportShowcase = { poster: string | null; images: Shot[]; privateSpace: boolean };

const shotSrc = (i: any): string | null =>
  i?.thumbnail_signed_src || i?.signed_src || i?.src || i?.download_url || null;

/** Public player document. `/thumb` 403s; this `image` is the still. Private spaces 404. */
async function fetchShowcase(id: string): Promise<MatterportShowcase> {
  let r: Response;
  try {
    r = await fetch(`https://my.matterport.com/api/v1/player/models/${encodeURIComponent(id)}`);
  } catch {
    return { poster: null, images: [], privateSpace: false };
  }
  if (r.status === 404) return { poster: null, images: [], privateSpace: true };
  if (!r.ok) return { poster: null, images: [], privateSpace: false };
  const data = await r.json();
  const images: Shot[] = (Array.isArray(data.images) ? data.images : [])
    .map((i: any) => {
      const src = shotSrc(i);
      if (!src) return null;
      return { id: String(i.sid || src), name: i.name || i.vision_generated_name || 'Photo', src };
    })
    .filter(Boolean);
  return { poster: (typeof data.image === 'string' && data.image) || images[0]?.src || null, images, privateSpace: false };
}

export const useMatterportShowcase = (id: string | null | undefined) =>
  useQuery({
    queryKey: ['matterport-showcase', id],
    enabled: !!id,
    queryFn: () => fetchShowcase(id as string),
    staleTime: 10 * 60 * 1000,
    retry: false,
  });

/** ponytail: one player fetch per model id. Private spaces have no public still; provider snapshots need a model-id admin route. */
export const MatterportThumb: React.FC<{ id?: string | null; alt: string; onClick?: () => void; fallbackSrc?: string | null }> = ({
  id,
  alt,
  onClick,
  fallbackSrc,
}) => {
  const { data, isLoading } = useMatterportShowcase(id);
  const shots = [...new Set([data?.poster, data?.images[0]?.src, fallbackSrc].filter((s): s is string => !!s))];
  const shotKey = shots.join('|');
  const [n, setN] = React.useState(0);
  React.useEffect(() => setN(0), [shotKey]);
  const src = shots[n];
  const box = isLoading && !src ? (
    <div className="h-12 w-16 animate-pulse rounded bg-gray-100" aria-hidden />
  ) : (
    <Thumb src={src} alt={alt} onError={() => setN((i) => i + 1)} />
  );
  if (!onClick) return box;
  return (
    <button type="button" onClick={onClick} className="block rounded focus-visible:outline focus-visible:outline-2 focus-visible:outline-blue-500" aria-label={`Preview ${alt}`}>
      {box}
    </button>
  );
};

export const ModelPreviewDialog: React.FC<{
  open: boolean;
  onClose: () => void;
  id: string;
  name: string | null;
}> = ({ open, onClose, id, name }) => {
  const [shown, setShown] = React.useState({ id, name });
  if (id && (id !== shown.id || name !== shown.name)) setShown({ id, name });
  const { data, isLoading } = useMatterportShowcase(shown.id || null);
  const [active, setActive] = React.useState<string | null>(null);
  React.useEffect(() => setActive(null), [shown.id]);
  const shots = data?.images ?? [];
  const photo = shots.find((s) => s.id === active);

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-h-[90vh] max-w-4xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{shown.name || 'Matterport space'}</DialogTitle>
          <DialogDescription className="font-mono text-xs">{shown.id}</DialogDescription>
        </DialogHeader>
        <iframe
          src={shown.id ? matterportShowUrl(shown.id) : undefined}
          title={shown.name || shown.id}
          className="aspect-video w-full rounded border border-gray-200"
          allow="fullscreen; xr-spatial-tracking"
        />
        {isLoading ? (
          <p className="text-sm text-gray-500">Loading photos…</p>
        ) : data?.privateSpace ? (
          <p className="text-sm text-gray-600">
            This space is private on Matterport, so it has no public photo. The player above only loads for an account that can open it.
          </p>
        ) : shots.length === 0 ? (
          <p className="text-sm text-gray-500">No photos published for this space.</p>
        ) : (
          <div className="space-y-2">
            {photo && <img src={photo.src} alt={photo.name} className="max-h-80 w-full rounded border bg-gray-50 object-contain" />}
            <div className="grid grid-cols-4 gap-2 sm:grid-cols-6">
              {shots.map((s) => (
                <button
                  key={s.id}
                  type="button"
                  title={s.name}
                  onClick={() => setActive(s.id === active ? null : s.id)}
                  className={`overflow-hidden rounded border ${active === s.id ? 'border-blue-500' : 'border-gray-200'}`}
                >
                  <img src={s.src} alt={s.name} className="h-16 w-full object-cover" />
                </button>
              ))}
            </div>
            <p className="text-xs text-gray-400">{shots.length} photos</p>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
};
